import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { ARQualificationDetails, ARVisaApplicationDetail } from './components';
import { QUALIFICATION_MODULE } from './constants';
import { AR_QUALIFICATION_CHECK_TEXT } from './lang';
import { useGetApplicationToDoList } from '../Application/graphql';
import { APPLICATION_TODO_LIST } from '../Application/components/ApplicationStatus/constants';
import { ApplicantParticulars, AssessmentSidebar } from '../Assessment';
import {
  Alert,
  type SidebarData,
  Button,
  Loading,
  COMMON_PAGE_TEXT,
} from '../../components';

export const ARQualificationCheckFormPage: React.FunctionComponent = () => {
  const { applicationId = '' } = useParams();

  const searchParams = new URLSearchParams(location.search);
  const sidebarActivated = searchParams.get('am');
  const targetModule =
    sidebarActivated === null
      ? QUALIFICATION_MODULE.QUALIFICATION_CHECK
      : Object.values(QUALIFICATION_MODULE)[Number(sidebarActivated)];

  const [activatedModuleId, setActivatedModuleId] = useState(
    targetModule as QUALIFICATION_MODULE,
  );

  const { applicationToDoList, loading, error } = useGetApplicationToDoList(
    applicationId ?? '',
  );

  const { visibleList } = applicationToDoList;
  const disableVisaApplication = !visibleList.includes(
    APPLICATION_TODO_LIST.VISA_APPLICATION,
  );

  const handleChangeActivated = (activatedId: string): void => {
    setActivatedModuleId(activatedId as QUALIFICATION_MODULE);
  };

  const handleBack = (): void => {
    window.location.href = '/internal/ar-homepage/ar-process';
  };

  const sidebarData: SidebarData[] = [
    {
      id: QUALIFICATION_MODULE.APPLICANT_PARTICULARS,
      name: AR_QUALIFICATION_CHECK_TEXT.APPLICANT_PARTICULARS,
    },
    {
      id: QUALIFICATION_MODULE.QUALIFICATION_CHECK,
      name: AR_QUALIFICATION_CHECK_TEXT.QUALIFICATION_CHECK,
    },
    {
      id: QUALIFICATION_MODULE.VISA_APPLICATION,
      name: AR_QUALIFICATION_CHECK_TEXT.VISA_APPLICATION,
      disabled: disableVisaApplication,
    },
  ];

  const arModulesMap: Partial<
    Record<QUALIFICATION_MODULE, { component: any; moduleTitle: string }>
  > = {
    [QUALIFICATION_MODULE.APPLICANT_PARTICULARS]: {
      component: <ApplicantParticulars showGoDetail />,
      moduleTitle: AR_QUALIFICATION_CHECK_TEXT.APPLICANT_PARTICULARS,
    },
    [QUALIFICATION_MODULE.QUALIFICATION_CHECK]: {
      component: <ARQualificationDetails applicationId={applicationId} />,
      moduleTitle: AR_QUALIFICATION_CHECK_TEXT.QUALIFICATION_CHECK,
    },
    [QUALIFICATION_MODULE.VISA_APPLICATION]: {
      component: <ARVisaApplicationDetail />,
      moduleTitle: AR_QUALIFICATION_CHECK_TEXT.VISA_APPLICATION,
    },
  };

  const activatedModule: { component: any; moduleTitle: string } | undefined =
    arModulesMap[activatedModuleId];

  const moduleComponent = activatedModule?.component ?? activatedModuleId;
  const moduleTitle = activatedModule?.moduleTitle ?? activatedModuleId;

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <PageContainer>
          <div className="sidebar-area">
            <AssessmentSidebar
              onChangeActivated={handleChangeActivated}
              currentActivatedModule={activatedModuleId}
              collapsible={true}
              drawerWidth={240}
              drawerInnerWidth="14.5rem"
              sidebarData={sidebarData}
              buttonField={
                <div className="submit-area">
                  <Button variant="outlined" onClick={handleBack}>
                    {COMMON_PAGE_TEXT.BUTTON.BACK}
                  </Button>
                </div>
              }
            />
          </div>
          <Container>
            <h1>{moduleTitle}</h1>
            <div className="form-area">{moduleComponent}</div>
          </Container>
        </PageContainer>
      )}
    </>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: row;

  .sidebar-area {
    width: min-content;
  }

  h1 {
    margin-top: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    text-align: center;
    color: #951d1d;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;

  .form-area {
    flex: 0.95;
    margin: 1rem;
  }

  .submit-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;
    margin-top: 2rem;
    button {
      margin: 0 1rem;
    }
  }
`;
